import { PensionProduct } from '../interfaces/PensionProduct';
import { InsurancePolicy } from '../interfaces/InsurancePolicy';

/**
 * Interface for duplicate product groups
 */
export interface DuplicateProductGroup {
    key: string;
    pension?: string;
    company: string;
    productType: string;
    policyNumber: string;
    products: PensionProduct[];
    count: number;
}

/**
 * Interface for duplicate insurance policy groups
 */
export interface DuplicateInsurancePolicyGroup {
    key: string;
    insurance?: string;
    company: string;
    branch: string;
    policyNumber: string;
    policies: InsurancePolicy[];
    count: number;
}

/**
 * Find all pension products that share the same combination of:
 * pension, company, productType, and policyNumber
 *
 * @param products - Array of PensionProduct objects to analyze
 * @returns Array of duplicate groups, each containing products with the same key combination
 */
export const findDuplicatePensionProducts = (products: PensionProduct[]): DuplicateProductGroup[] => {
    // Create a map to group products by their key combination
    const productMap = new Map<string, PensionProduct[]>();

    products.forEach((product) => {
        // Extract the key fields
        const pensionId = typeof product.pension === 'string' ? product.pension : product.pension?._id || 'no-pension';
        const companyName = product.company?.name || 'no-company';
        const productType = product.productType || 'no-type';
        const policyNumber = product.policyNumber || 'no-policy-number';

        // Create a unique key from the combination
        const key = `${pensionId}|${companyName}|${productType}|${policyNumber}`;

        // Add product to the group
        if (!productMap.has(key)) {
            productMap.set(key, []);
        }
        productMap.get(key)!.push(product);
    });

    // Filter to only include groups with duplicates (count > 1)
    const duplicateGroups: DuplicateProductGroup[] = [];

    productMap.forEach((products, key) => {
        if (products.length > 1) {
            const [pensionId, companyName, productType, policyNumber] = key.split('|');
            duplicateGroups.push({
                key,
                pension: pensionId !== 'no-pension' ? pensionId : undefined,
                company: companyName,
                productType,
                policyNumber,
                products,
                count: products.length,
            });
        }
    });

    return duplicateGroups;
};

/**
 * Find all insurance policies that share the same combination of:
 * insurance, company, branch, and policyNumber
 *
 * @param policies - Array of InsurancePolicy objects to analyze
 * @returns Array of duplicate groups, each containing policies with the same key combination
 */
export const findDuplicateInsurancePolicies = (policies: InsurancePolicy[]): DuplicateInsurancePolicyGroup[] => {
    // Create a map to group policies by their key combination
    const policyMap = new Map<string, InsurancePolicy[]>();

    policies.forEach((policy) => {
        // Extract the key fields
        const insuranceId = typeof policy.insurance === 'string' ? policy.insurance : policy.insurance?._id || 'no-insurance';
        const companyId = typeof policy.company === 'string' ? policy.company : policy.company?._id || 'no-company';
        const branch = policy.branch || 'no-branch';
        const policyNumber = policy.policyNumber || 'no-policy-number';

        // Create a unique key from the combination
        const key = `${insuranceId}|${companyId}|${branch}|${policyNumber}`;

        // Add policy to the group
        if (!policyMap.has(key)) {
            policyMap.set(key, []);
        }
        policyMap.get(key)!.push(policy);
    });

    // Filter to only include groups with duplicates (count > 1)
    const duplicateGroups: DuplicateInsurancePolicyGroup[] = [];

    policyMap.forEach((policies, key) => {
        if (policies.length > 1) {
            const [insuranceId, companyId, branch, policyNumber] = key.split('|');
            duplicateGroups.push({
                key,
                insurance: insuranceId !== 'no-insurance' ? insuranceId : undefined,
                company: companyId,
                branch,
                policyNumber,
                policies,
                count: policies.length,
            });
        }
    });

    return duplicateGroups;
};

/**
 * Print duplicate pension products in a readable format
 *
 * @param duplicateGroups - Array of duplicate product groups
 */
export const printDuplicatePensionProducts = (duplicateGroups: DuplicateProductGroup[]): void => {
    if (duplicateGroups.length === 0) {
        console.log('No duplicate pension products found.');
        return;
    }

    console.log(`\n=== Found ${duplicateGroups.length} duplicate pension product groups ===\n`);

    duplicateGroups.forEach((group, index) => {
        console.log(`Duplicate Group ${index + 1}:`);
        console.log(`  Company: ${group.company}`);
        console.log(`  Product Type: ${group.productType}`);
        console.log(`  Policy Number: ${group.policyNumber}`);
        console.log(`  Pension ID: ${group.pension || 'N/A'}`);
        console.log(`  Count: ${group.count} products`);
        console.log(`  Product IDs: ${group.products.map(p => p._id).join(', ')}`);
        console.log('');
    });
};

/**
 * Print duplicate insurance policies in a readable format
 *
 * @param duplicateGroups - Array of duplicate policy groups
 */
export const printDuplicateInsurancePolicies = (duplicateGroups: DuplicateInsurancePolicyGroup[]): void => {
    if (duplicateGroups.length === 0) {
        console.log('No duplicate insurance policies found.');
        return;
    }

    console.log(`\n=== Found ${duplicateGroups.length} duplicate insurance policy groups ===\n`);

    duplicateGroups.forEach((group, index) => {
        console.log(`Duplicate Group ${index + 1}:`);
        console.log(`  Company: ${group.company}`);
        console.log(`  Branch: ${group.branch}`);
        console.log(`  Policy Number: ${group.policyNumber}`);
        console.log(`  Insurance ID: ${group.insurance || 'N/A'}`);
        console.log(`  Count: ${group.count} policies`);
        console.log(`  Policy IDs: ${group.policies.map(p => p._id).join(', ')}`);
        console.log('');
    });
};

/**
 * Example usage for checking duplicates in a pension round
 */
export const checkPensionProductDuplicates = async (pensionId: string) => {
    const { getProducts } = await import('./api/products');

    try {
        const results = await getProducts({ pensionId });
        const duplicates = findDuplicatePensionProducts(results);
        printDuplicatePensionProducts(duplicates);
        return duplicates;
    } catch (error) {
        console.error('Error checking pension product duplicates:', error);
        throw error;
    }
};

/**
 * Example usage for checking duplicates in an insurance round
 */
export const checkInsurancePolicyDuplicates = async (insuranceId: string) => {
    const { getInsurancePolicies } = await import('./api/insurancePolicy');

    try {
        const response = await getInsurancePolicies({ insuranceId });
        const policies = response.policies || [];
        const duplicates = findDuplicateInsurancePolicies(policies);
        printDuplicateInsurancePolicies(duplicates);
        return duplicates;
    } catch (error) {
        console.error('Error checking insurance policy duplicates:', error);
        throw error;
    }
};
